import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Image as ImageIcon, Lock, Globe, 
  Loader2, MapPin, FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Header from '@/components/Header';
import api from '@/utils/api';
import { toast } from 'sonner';

const GROUP_CATEGORIES = [
  { id: "buy_sell", name: "Buy & Sell", icon: "💰", description: "For buying and selling items" },
  { id: "collectors", name: "Collectors", icon: "🏆", description: "For collectors and enthusiasts" },
  { id: "local", name: "Local Community", icon: "📍", description: "Location-based community" },
  { id: "fashion", name: "Fashion & Style", icon: "👗", description: "Fashion lovers and resellers" },
  { id: "electronics", name: "Electronics", icon: "📱", description: "Tech and electronics" },
  { id: "vehicles", name: "Vehicles", icon: "🚗", description: "Cars, bikes, and more" },
  { id: "home_garden", name: "Home & Garden", icon: "🏠", description: "Home improvement and gardening" },
  { id: "sports", name: "Sports & Outdoors", icon: "⚽", description: "Sports equipment and outdoor gear" },
  { id: "hobbies", name: "Hobbies & Crafts", icon: "🎨", description: "Hobbies, crafts, and DIY" },
  { id: "other", name: "Other", icon: "📦", description: "Other communities" },
];

const CreateGroupPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    privacy: 'public',
    category: 'buy_sell',
    rules: '',
    cover_image: '',
    location: ''
  });

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await api.get('/auth/me');
        setUser(res.data);
      } catch {
        toast.error('Please sign in to create a group');
        navigate('/auth');
      }
    };
    fetchUser();
  }, [navigate]);

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const res = await api.post('/upload', formDataUpload);
      setFormData(prev => ({ ...prev, cover_image: res.data.url }));
      toast.success('Cover image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Group name is required');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Group description is required');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/groups', formData);
      toast.success('Group created successfully!');
      navigate(`/community/groups/${res.data.group.group_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create group');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header user={user} setUser={setUser} />
      
      {/* Spacer for fixed header */}
      <div className="h-[184px]"></div>
      
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/community/groups')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Groups
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Create a New Group</CardTitle>
            <p className="text-gray-500">
              Build a community around shared interests
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Cover Image */}
              <div>
                <Label>Cover Image</Label>
                <div 
                  className="mt-2 relative h-40 bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:bg-gray-200 transition"
                  onClick={() => document.getElementById('cover-upload').click()}
                >
                  {formData.cover_image ? (
                    <img 
                      src={formData.cover_image} 
                      alt="Cover" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                      {uploading ? (
                        <Loader2 className="h-8 w-8 animate-spin" />
                      ) : (
                        <>
                          <ImageIcon className="h-8 w-8 mb-2" />
                          <span className="text-sm">Click to upload cover image</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <input
                  id="cover-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleCoverUpload}
                />
              </div>

              {/* Group Name */}
              <div>
                <Label htmlFor="name">Group Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., Vintage Watch Collectors"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  maxLength={100}
                  className="mt-1"
                />
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  placeholder="What is this group about? Who should join?"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={4}
                  maxLength={500}
                  className="mt-1"
                />
                <p className="text-xs text-gray-400 mt-1">
                  {formData.description.length}/500 characters
                </p>
              </div>

              {/* Category */}
              <div>
                <Label>Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(val) => setFormData(prev => ({ ...prev, category: val }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {GROUP_CATEGORIES.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        <div className="flex items-center gap-2">
                          <span>{cat.icon}</span>
                          <span>{cat.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Privacy */}
              <div>
                <Label>Privacy</Label>
                <RadioGroup
                  value={formData.privacy}
                  onValueChange={(val) => setFormData(prev => ({ ...prev, privacy: val }))}
                  className="mt-2 space-y-3"
                >
                  <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-gray-50 cursor-pointer">
                    <RadioGroupItem value="public" id="public" className="mt-1" />
                    <div className="flex-1">
                      <Label htmlFor="public" className="flex items-center gap-2 cursor-pointer">
                        <Globe className="h-4 w-4 text-emerald-600" />
                        <span className="font-medium">Public</span>
                      </Label>
                      <p className="text-sm text-gray-500 mt-1">
                        Anyone can see posts. Users can join directly.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-gray-50 cursor-pointer">
                    <RadioGroupItem value="private" id="private" className="mt-1" />
                    <div className="flex-1">
                      <Label htmlFor="private" className="flex items-center gap-2 cursor-pointer">
                        <Lock className="h-4 w-4 text-amber-600" />
                        <span className="font-medium">Private</span>
                      </Label>
                      <p className="text-sm text-gray-500 mt-1">
                        Only members can see posts. Users must request to join.
                      </p>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              {/* Location (Optional) */}
              <div>
                <Label htmlFor="location">Location (Optional)</Label>
                <div className="relative mt-1">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="location"
                    placeholder="e.g., New York, NY"
                    value={formData.location}
                    onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Group Rules (Optional) */}
              <div>
                <Label htmlFor="rules">Group Rules (Optional)</Label>
                <Textarea
                  id="rules"
                  placeholder="Set guidelines for your community..."
                  value={formData.rules}
                  onChange={(e) => setFormData(prev => ({ ...prev, rules: e.target.value }))}
                  rows={4}
                  className="mt-1"
                />
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => navigate('/community/groups')}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Creating...
                    </>
                  ) : (
                    'Create Group'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CreateGroupPage;
